import React, {
    FC,
    useEffect,
} from 'react';

import { observer } from 'mobx-react';
import { when } from 'mobx';

import i18n from '@/content';
import TabTitle from '../../Common/TabTitle';
import ErrorMessage from '../../Common/ErrorMessage/ErrorMessage';
import Spinner from '../../Common/Spin';
import PolicySettings from './PolicySettings';
import PolicyButtons from './PolicyButtons';
import PolicyEditorSidebar from './PolicyEditorSidebar';
import ConflictModal from './ConflictModal';
import { useStores } from '../../hooks';
import styles from './PolicyEditor.module.scss';

const nameSpace = 'policyEditor';

type ReactionPromise = Promise<void> & { cancel(): void };

const PolicyEditor: FC = observer(() => {
    const { policyStore } = useStores();

    const {
        selectedPolicy,
        loadingPolicy,
        hasFilePolicyError,
        selectPolicyForEditing,
    } = policyStore;

    useEffect(() => {
        let reactionPromise: ReactionPromise | null = null;
        let isMounted = true;

        const initializePolicy = async (): Promise<void> => {
            if (policyStore.loadingPolicy) {
                // Assign synchronously before await to ensure cleanup can access it
                reactionPromise = when(() => !policyStore.loadingPolicy);
                try {
                    await reactionPromise;
                } catch (error) {
                    // Reaction was cancelled, which is fine
                    return;
                }
            } else {
                await policyStore.fetchPolicyList();
            }

            // Only select policy if component is still mounted
            if (isMounted) {
                selectPolicyForEditing();
            }
        };

        initializePolicy();

        return () => {
            isMounted = false;
            policyStore.unmount();
            if (reactionPromise) {
                reactionPromise.cancel();
            }
        };
    }, []);

    const title = i18n.t(`${nameSpace}.title`);

    return (
        <Spinner spinning={loadingPolicy} screenCentered>
            <TabTitle title={title} />
            <div data-testid="policyEditor_container" className={styles['policy-editor-wrapper']}>
                <div className={styles['policy-editor']}>
                    <PolicyEditorSidebar title={title} />
                    <div className={styles['policy-settings-container']}>
                        {hasFilePolicyError
                            ? (
                                <ErrorMessage
                                    title={i18n.t('general.errors.1', { type: title })}
                                    subtitle={i18n.t('general.errors.try')}
                                />
                            )
                            : selectedPolicy && <PolicySettings />}
                        {selectedPolicy && <PolicyButtons />}
                    </div>
                </div>
            </div>
            <ConflictModal />
        </Spinner>
    );
});

export default PolicyEditor;
